/**
 * The phone's SPACE section: presence, queue, ladder, supplies, guests, notes.
 *
 * Space mode sits ABOVE the tabs rather than inside one, because the spec asks
 * for a one-tap switcher and a switcher hidden behind a tab costs two.
 *
 * The tabs are real ARIA tabs with arrow-key navigation — this is a phone
 * surface, but the same components run on a laptop and a keyboard user should
 * not be stuck.
 */

import { useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { CheckIn } from './CheckIn.js'
import { Queue } from './Queue.js'
import { Ladder } from './Ladder.js'
import { Supplies } from './Supplies.js'
import { Guests } from './Guests.js'
import { Notes } from './Notes.js'
import { SpaceModeSwitch } from './SpaceMode.js'
import { SpaceStyles, type SpaceScreenProps } from './spaceData.js'

export type SpaceTabKey = 'presence' | 'queue' | 'ladder' | 'supplies' | 'guests' | 'notes'

export const SPACE_TABS: readonly { key: SpaceTabKey; label: string }[] = [
  { key: 'presence', label: 'Presence' },
  { key: 'queue', label: 'Queue' },
  { key: 'ladder', label: 'Ladder' },
  { key: 'supplies', label: 'Supplies' },
  { key: 'guests', label: 'Guests' },
  { key: 'notes', label: 'Notes' },
]

export interface SpaceProps extends SpaceScreenProps {
  readonly initialTab?: SpaceTabKey
  /** Forwarded to every screen; 0 freezes every clock-driven readout. */
  readonly tickMs?: number
}

export function Space(props: SpaceProps) {
  const { initialTab, tickMs, ...screen } = props
  const [tab, setTab] = useState<SpaceTabKey>(initialTab ?? 'presence')
  const baseId = useId()
  const tabRefs = useRef(new Map<SpaceTabKey, HTMLButtonElement>())

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const index = SPACE_TABS.findIndex(t => t.key === tab)
    let next = index
    if (e.key === 'ArrowRight') next = (index + 1) % SPACE_TABS.length
    else if (e.key === 'ArrowLeft') next = (index - 1 + SPACE_TABS.length) % SPACE_TABS.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = SPACE_TABS.length - 1
    else return
    e.preventDefault()
    const key = SPACE_TABS[next]?.key
    if (key === undefined) return
    setTab(key)
    tabRefs.current.get(key)?.focus()
  }

  const forwarded = tickMs === undefined ? screen : { ...screen, tickMs }

  return (
    <div className="sp" data-testid="space-root">
      <SpaceStyles />
      <SpaceModeSwitch {...forwarded} />

      <div className="sp-tabs" role="tablist" aria-label="Space" onKeyDown={onKeyDown}>
        {SPACE_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`${baseId}-tab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls={`${baseId}-panel-${t.key}`}
            tabIndex={tab === t.key ? 0 : -1}
            ref={el => {
              if (el === null) tabRefs.current.delete(t.key)
              else tabRefs.current.set(t.key, el)
            }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`${baseId}-panel-${tab}`}
        aria-labelledby={`${baseId}-tab-${tab}`}
        tabIndex={0}
      >
        {tab === 'presence' ? <CheckIn {...forwarded} /> : null}
        {tab === 'queue' ? <Queue {...forwarded} /> : null}
        {tab === 'ladder' ? <Ladder {...forwarded} /> : null}
        {tab === 'supplies' ? <Supplies {...forwarded} /> : null}
        {tab === 'guests' ? <Guests {...forwarded} /> : null}
        {tab === 'notes' ? <Notes {...forwarded} /> : null}
      </div>
    </div>
  )
}
